import React from 'react'

interface NavigationTab {
  id: string
  icon: React.ReactNode
  label: string
  count?: number
  isActive: boolean
}

interface FloatingNavigationProps {
  tabs: NavigationTab[]
  onTabClick: (tabId: string) => void
}

const FloatingNavigation: React.FC<FloatingNavigationProps> = ({ tabs, onTabClick }) => {
  return (
    <>
      {/* Плавающее меню для компьютерной версии - компактный фон, крупные элементы */}
      <div className="hidden md:block fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
        <nav className="bg-white/95 backdrop-blur-lg rounded-xl shadow-lg border border-slate-200/50 px-1 py-1">
          <div className="flex items-center space-x-0.5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabClick(tab.id)}
                className={`
                  relative flex flex-col items-center justify-center px-2 py-1.5 rounded-lg transition-all duration-300 min-w-[60px] group
                  ${tab.isActive 
                    ? 'text-blue-600' 
                    : 'text-slate-500 hover:text-blue-600 hover:bg-slate-50'
                  }
                `}
              >
                {/* Иконка крупная с компактным фоном */}
                <div className="relative w-8 h-8 flex items-center justify-center mb-1">
                  {/* Компактный фон для активной иконки */}
                  {tab.isActive && (
                    <div className="absolute inset-0 bg-blue-100 rounded-md"></div>
                  )}
                  
                  {/* Крупная иконка */}
                  <div className="text-xl leading-none relative z-10">
                    {tab.icon}
                  </div>
                  {typeof tab.count === 'number' && tab.count > 0 && (
                    <span className="absolute -top-2 -right-2 z-20 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow">
                      {tab.count > 99 ? '99+' : tab.count}
                    </span>
                  )}
                </div>
                
                {/* Крупный лейбл под иконкой */}
                <span className={`
                  text-sm font-medium transition-all duration-300
                  ${tab.isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-blue-600'}
                `}>
                  {tab.label}
                </span>
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Навигация для мобильных устройств - точно как в мини-приложении */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-t border-slate-200">
        <nav className="max-w-4xl mx-auto px-4 flex justify-around h-16">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabClick(tab.id)}
              className={`
                flex flex-col items-center justify-center w-1/5 relative py-2 transition-colors duration-200
                ${tab.isActive ? 'text-blue-600' : 'text-slate-500 hover:text-blue-600'}
              `}
            >
              <div className="flex flex-col items-center">
                {/* Иконка с красным бейджем для мобильной */}
                <div className="relative w-10 h-10 flex items-center justify-center mb-1">
                  {/* Фон для активной иконки в мобильной версии */}
                  {tab.isActive && (
                    <div className="absolute inset-0 bg-blue-100 rounded-2xl"></div>
                  )}
                  
                  {/* Иконка */}
                  <div className="text-2xl leading-none relative z-10">
                    {tab.icon}
                  </div>
                  {typeof tab.count === 'number' && tab.count > 0 && (
                    <span className="absolute -top-2 -right-2 z-20 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow">
                      {tab.count > 99 ? '99+' : tab.count}
                    </span>
                  )}
                </div>
                
                {/* Лейбл */}
                <span className={`text-xs font-medium leading-tight ${tab.isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                  {tab.label}
                </span>
              </div>
            </button>
          ))}
        </nav>
        
        {/* Отступ для iPhone */}
        <div className="h-safe-area-inset-bottom"></div>
      </div>
    </>
  )
}

export default FloatingNavigation
